import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Security,
  Edit,
  CheckCircle,
  Warning,
  AdminPanelSettings,
  Lock,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { authAPI } from '@/services/authAPI';
import { usePermissions } from '@/hooks/usePermissions';
import { useAppDispatch } from '@/store/hooks';
import { updateUser } from '@/store/slices/authSlice';

export const UserProfilePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);

  const { isSuperAdmin, can } = usePermissions();
  const canManageAdmins = isSuperAdmin() || can('admin_staff', 'view');

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
  });

  // Password change state
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });

  const handlePasswordFieldChange = (field: string, value: string) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialog(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    setPasswordError('');
    setPasswordSuccess('');
    setShowPasswords({ currentPassword: false, newPassword: false, confirmNewPassword: false });
  };

  const handleSubmitPasswordChange = async () => {
    const { currentPassword, newPassword, confirmNewPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (newPassword.length < 12) {
      setPasswordError('Password must be at least 12 characters long');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    const result = await authAPI.changePassword(currentPassword, newPassword, confirmNewPassword);

    setPasswordLoading(false);

    if (result.success) {
      setPasswordSuccess(result.message);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    } else {
      setPasswordError(result.message);
    }
  };

  // Fetch current profile data
  useEffect(() => {
    fetchProfile();
  }, []);

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const fetchProfile = async () => {
    setPageLoading(true);
    setError('');
    
    try {
      const result = await authAPI.getCurrentProfile();
      
      if (result.success && result.user) {
        setUser(result.user);
        // Sync to Redux so dashboard banner updates too
        dispatch(updateUser(result.user));
      } else {
        console.error('❌ Profile load failed:', result.message);
        setError(result.message || 'Failed to load profile');
      }
    } catch (error) {
      console.error('❌ Profile fetch error:', error);
      setError('Failed to load profile. Please try again.');
    } finally {
      setPageLoading(false);
    }
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await authAPI.updateProfile(formData);
      
      if (result.success) {
        setSuccess(result.message);
        setEditing(false);
        // Refresh profile data to show updated information
        await fetchProfile();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: user?.fullName || '',
      email: user?.email || '',
      phone: user?.phone || '',
    });
    setEditing(false);
    setError('');
    setSuccess('');
  };

  const getProfileCompletionPercentage = () => {
    const fields = [user?.fullName, user?.email, user?.phone];
    const filledFields = fields.filter(field => field && field.trim() !== '');
    return Math.round((filledFields.length / fields.length) * 100);
  };

  const getMissingFields = () => {
    const missing = [];
    if (!user?.fullName) missing.push('Full Name');
    if (!user?.email) missing.push('Email');
    if (!user?.phone) missing.push('Phone Number');
    return missing;
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date().getTime();
    const past = new Date(dateString).getTime();
    const diffInHours = Math.floor((now - past) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w ago`;
  };

  const profileCompletion = getProfileCompletionPercentage();
  const missingFields = getMissingFields();

  // Show loading spinner while fetching profile
  if (pageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error if profile couldn't be loaded
  if (!user && error) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchProfile}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar sx={{ mr: 2, width: 56, height: 56, bgcolor: 'primary.main' }}>
          <Person sx={{ fontSize: 32 }} />
        </Avatar>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Admin Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your admin account settings and preferences
          </Typography>
        </Box>
      </Box>

      {/* Other admins are managed in one place: Security → Staff Management */}
      {canManageAdmins && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="outlined" startIcon={<AdminPanelSettings />} onClick={() => navigate('/admin/staff')}>
            Manage admin users
          </Button>
        </Box>
      )}

      {(
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>

      {/* Profile Completion Card */}
      {profileCompletion < 100 && (
        <Card sx={{ mb: 3, borderLeft: 4, borderLeftColor: 'warning.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Warning color="warning" sx={{ mr: 1 }} />
              <Typography variant="h6">Complete Admin Profile</Typography>
              <Chip 
                label={`${profileCompletion}% Complete`}
                color={profileCompletion >= 50 ? 'primary' : 'warning'}
                size="small"
                sx={{ ml: 'auto' }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Complete your admin profile for better security and account recovery options.
            </Typography>
            {missingFields.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Missing information:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {missingFields.map((field) => (
                    <Chip
                      key={field}
                      label={field}
                      size="small"
                      variant="outlined"
                      color="warning"
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alert Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Profile Information Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Admin Information</Typography>
          {!editing ? (
            <Button
              startIcon={<Edit />}
              variant="outlined"
              onClick={() => setEditing(true)}
            >
              Edit Profile
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : undefined}
              >
                Save Changes
              </Button>
            </Box>
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Person sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Full Name
              </Typography>
            </Box>
            {editing ? (
              <TextField
                fullWidth
                value={formData.fullName}
                onChange={handleChange('fullName')}
                placeholder="Enter your full name"
              />
            ) : (
              <Typography variant="body1">
                {user?.fullName || 'Not provided'}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Email sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Email Address
              </Typography>
              {user?.emailVerified && (
                <CheckCircle sx={{ ml: 1, fontSize: 16, color: 'success.main' }} />
              )}
            </Box>
            {editing ? (
              <TextField
                fullWidth
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                placeholder="Enter your email address"
              />
            ) : (
              <Typography variant="body1">
                {user?.email || 'Not provided'}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Phone sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Phone Number
              </Typography>
              {user?.phoneVerified && (
                <CheckCircle sx={{ ml: 1, fontSize: 16, color: 'success.main' }} />
              )}
            </Box>
            {editing ? (
              <TextField
                fullWidth
                value={formData.phone}
                onChange={handleChange('phone')}
                placeholder="Enter your phone number"
              />
            ) : (
              <Typography variant="body1">
                {user?.phone || 'Not provided'}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Security sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Account Status
              </Typography>
            </Box>
            <Chip
              label={user?.status || 'Unknown'}
              color={user?.status === 'active' ? 'success' : 'default'}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Account Information */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Account Information
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              User ID
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
              {user?.id}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Role
            </Typography>
            <Chip
              label={user?.role || 'User'}
              size="small"
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Member Since
            </Typography>
            <Typography variant="body2">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Last Login
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                {user?.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Unknown'}
              </Typography>
              {user?.lastLoginAt && (
                <Chip
                  size="small"
                  label={getTimeAgo(user.lastLoginAt)}
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Security Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Lock sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6">Security</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manage your password and account security settings.
        </Typography>
        <Button variant="outlined" onClick={() => setPasswordDialog(true)}>
          Change Password
        </Button>
      </Paper>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialog} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Current Password"
              type={showPasswords.currentPassword ? 'text' : 'password'}
              value={passwordForm.currentPassword}
              onChange={(e) => handlePasswordFieldChange('currentPassword', e.target.value)}
              disabled={passwordLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords(prev => ({ ...prev, currentPassword: !prev.currentPassword }))}
                      edge="end"
                    >
                      {showPasswords.currentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="New Password"
              type={showPasswords.newPassword ? 'text' : 'password'}
              value={passwordForm.newPassword}
              onChange={(e) => handlePasswordFieldChange('newPassword', e.target.value)}
              disabled={passwordLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords(prev => ({ ...prev, newPassword: !prev.newPassword }))}
                      edge="end"
                    >
                      {showPasswords.newPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type={showPasswords.confirmNewPassword ? 'text' : 'password'}
              value={passwordForm.confirmNewPassword}
              onChange={(e) => handlePasswordFieldChange('confirmNewPassword', e.target.value)}
              disabled={passwordLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirmNewPassword: !prev.confirmNewPassword }))}
                      edge="end"
                    >
                      {showPasswords.confirmNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {passwordError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {passwordError}
            </Alert>
          )}
          {passwordSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {passwordSuccess}
            </Alert>
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            Password must be at least 12 characters long and include uppercase, lowercase, numbers, and special characters.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={passwordLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitPasswordChange} disabled={passwordLoading}>
            {passwordLoading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      )}
    </Box>
  );
};